/**
 * La animación de las barras flotantes cuando cambian de tamaño.
 *
 * Las dos barras (canvas y documento) son un panel que crece y encoge solo: la
 * fila de opciones aparece cuando la herramienta tiene opciones, se va cuando
 * no, y cambia de ancho según cuáles sean. Sin animación ese cambio es un
 * salto: el panel está de un tamaño en un frame y de otro en el siguiente, y
 * como está anclado abajo y centrado, el salto se ve en los cuatro bordes.
 *
 * Lo que se anima es la CAJA, no lo de adentro. El contenido aparece de una y
 * queda quieto mientras el panel se abre encima suyo (`overflow: hidden` lo
 * recorta durante el movimiento): animar también los botones haría que la fila
 * llegue tarde a su propia apertura y que un cambio de opciones —que es
 * instantáneo para la mano— se sienta lento.
 *
 * Que el contenido quede quieto DE VERDAD lo consigue el CSS pegándolo al borde
 * de abajo mientras dura el morph (`justify-content: flex-end` en
 * `.is-morphing`). Sin eso el contenido se apila desde arriba, la fila de
 * herramientas queda fuera del recorte y sube hasta su lugar mientras la caja
 * crece: los íconos se mueven y el gesto se siente un tropiezo.
 *
 * La técnica es la de siempre para animar hacia `auto`: se mide el tamaño de
 * antes, se aplica el cambio, se mide el de después, y se anima entre los dos
 * números. Al terminar se sueltan los estilos en línea, así el panel vuelve a
 * medirse solo (una fuente que carga tarde, un zoom del navegador).
 */

/** Cuánto dura la apertura. Tiene que coincidir con la transición del CSS. */
const MORPH_MS = 220;

/** Timers pendientes por barra: un cambio nuevo cancela el que estaba en curso. */
const pending = new WeakMap<HTMLElement, number>();

function prefersReducedMotion(): boolean {
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
}

/** Deja la barra como estaba: sin tamaño impuesto y sin recorte. */
function release(root: HTMLElement): void {
  const timer = pending.get(root);
  if (timer !== undefined) {
    window.clearTimeout(timer);
    pending.delete(root);
  }
  root.classList.remove("is-morphing");
  root.style.width = "";
  root.style.height = "";
}

/**
 * Aplica `mutate` (lo que muestra u oculta filas) animando el tamaño del panel.
 *
 * `mutate` tiene que hacer TODO el cambio de una: se mide una vez antes y una
 * vez después, y lo que quede a medio camino se ve como el tamaño final.
 */
export function morphToolbar(root: HTMLElement, mutate: () => void): void {
  // Sin layout que medir (la barra todavía no está montada) o con el sistema
  // pidiendo quietud, el cambio es directo.
  if (!root.isConnected || prefersReducedMotion()) {
    release(root);
    mutate();
    return;
  }

  const before = root.getBoundingClientRect();
  // Si venía animando, el punto de partida es dónde está AHORA, no el tamaño
  // congelado: así una segunda apertura encima de la primera no pega un tirón.
  const fromW = before.width;
  const fromH = before.height;

  // Se sueltan los estilos impuestos para que la medición de después sea la
  // del tamaño natural del panel con el contenido nuevo.
  release(root);
  const openedPanels = collectPanels(root);
  mutate();

  const after = root.getBoundingClientRect();
  const toW = after.width;
  const toH = after.height;
  markEntering(openedPanels);

  // Nada cambió de tamaño (sólo se cambió un ícono, por ejemplo): no hay nada
  // que animar y no vale la pena forzar un reflow.
  if (Math.abs(toW - fromW) < 0.5 && Math.abs(toH - fromH) < 0.5) return;

  root.classList.add("is-morphing");
  root.style.width = `${fromW}px`;
  root.style.height = `${fromH}px`;
  void root.offsetWidth; // fuerza el reflow: sin esto el navegador ve un solo valor
  root.style.width = `${toW}px`;
  root.style.height = `${toH}px`;

  // Se libera por reloj y no por `transitionend`: son dos propiedades y la que
  // no cambió no dispara evento, así que esperar el evento dejaría el panel
  // clavado en píxeles la mitad de las veces.
  pending.set(
    root,
    window.setTimeout(() => release(root), MORPH_MS + 60),
  );
}

/** Las filas de opciones que estaban ocultas antes del cambio. */
function collectPanels(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(".toolbar-options")].filter((el) => el.hidden);
}

/**
 * Las filas que acaban de abrirse entran con un fundido corto. Es la caja que
 * aparece, no su contenido moviéndose: la fila ya está armada y quieta en su
 * lugar final, sólo pasa de invisible a visible mientras el panel crece.
 */
function markEntering(wereHidden: HTMLElement[]): void {
  for (const el of wereHidden) {
    if (el.hidden) continue;
    el.classList.remove("is-entering");
    void el.offsetWidth; // reinicia la animación si se abre dos veces seguidas
    el.classList.add("is-entering");
    el.addEventListener("animationend", () => el.classList.remove("is-entering"), { once: true });
  }
}
